import React, { useState } from 'react';
import { m } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiZap, FiCalendar, FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { checkClosingPeriod } from '../../data/apiAdapter';
import { isStoreCurrentlyOpen } from '../../utils/openingHours';
import { getLocationOpeningHours } from '../../utils/location';
import { useMotionConfig } from '../../hooks/useReducedMotion';

const RentalTiming = ({selectedTiming,onTimingSelect,onBack,location,merchantConfig}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { getLocationTranslation } = useLanguage();
  const motion = useMotionConfig();

  // Translate the location name to the selected language (falls back to the
  // main-language name when no translation exists for the current language).
  const locationName = location
    ? getLocationTranslation(
        location.name,
        location.location_translations,
        'location_name'
      ) || location.name
    : '';
  const [errorMessage, setErrorMessage] = useState(null); // Show error only after clicking "Start Now"

  // Handle timing selection with validation for "Start Now"
  const handleTimingSelect = async (timingId) => {
    // Clear any previous error
    setErrorMessage(null);

    // If user selected "Make a Reservation", proceed immediately
    if (timingId !== 'immediate') {
      onTimingSelect(timingId);
      return;
    }

    // For "Start Now", check if store is currently open
    // Check closing periods first
    if (location?.id) {
      const today = new Date();
      const closingStatus = await checkClosingPeriod(location.id, today);

      if (closingStatus.isClosed) {
        setErrorMessage(closingStatus.reason || t('time.storeClosed'));
        return; // Don't proceed
      }
    }

    // Then check opening hours
    const openingHours = getLocationOpeningHours(location);
    const timezone = merchantConfig?.timezone;

    if (openingHours) {
      const openStatus = isStoreCurrentlyOpen(openingHours, timezone);

      if (!openStatus.isOpen) {
        const translatedMessage = openStatus.messageKey
          ? t(openStatus.messageKey, openStatus.messageParams)
          : openStatus.message;
        setErrorMessage(translatedMessage || t('time.storeCurrentlyClosed'));
        return; // Don't proceed
      }
    }

    // All checks passed - proceed with immediate rental
    onTimingSelect(timingId);
  };

  const timingOptions = [
    {
      id: 'immediate',
      title: t('timing.immediate'),
      subtitle: t('timing.immediateDesc'),
      icon: FiZap,
      colorType: 'secondary', // Uses color2/green
      benefits: [
        t('timing.immediateInstantAccess'),
        t('timing.immediateNoWaiting'),
        t('timing.immediateSpontaneous')
      ]
    },
    {
      id: 'reservation',
      title: t('timing.scheduled'),
      subtitle: t('timing.scheduledDesc'),
      icon: FiCalendar,
      colorType: 'primary', // Uses color1/blue
      benefits: [
        t('timing.scheduledGuaranteed'),
        t('timing.scheduledPlanAhead'),
        t('timing.scheduledBestSelection')
      ],
      recommended: true
    }
  ];

  // If there's an error, show full-page error message instead of timing options
  if (errorMessage) {
    return (
      <div className="max-w-3xl mx-auto">
        <m.div
          {...motion.fadeInUp}
          className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-8 text-center"
        >
          <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-yellow-600 mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-xl font-bold text-gray-900 mb-3">{t('time.storeCurrentlyClosed')}</h2>
          <p className="text-gray-700 mb-4">{errorMessage}</p>
          <p className="text-gray-600 text-sm mb-6">
            {t('time.noImmediateRental')}
          </p>
          <button
            onClick={() => setErrorMessage(null)}
            className="px-6 py-3 rounded-lg font-semibold transition-all duration-200"
            style={{ background: theme.colors.primary.gradient, color: theme.colors.primary.contrastText }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            aria-label={t('time.goBackMakeReservation')}
          >
            {t('time.goBackMakeReservation')}
          </button>
        </m.div>
      </div>
    );
  }

  // Normal timing selection UI
  return (
    <div className="max-w-3xl mx-auto">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          aria-label="Back to location selection"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" aria-hidden="true" />
          <span>{t('timing.backToLocations')}</span>
        </button>
      )}

      <m.div
        {...motion.fadeInUp}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('timing.nowOrLater')}</h1>
        <p className="text-gray-600">
          {t('timing.chooseWhen')}{locationName && ` (${locationName})`}
        </p>
      </m.div>

      <div className="grid md:grid-cols-2 gap-6">
        {timingOptions.map((option,index) => {
          // Create descriptive aria-label for screen readers
          const ariaLabel = `${option.title}, ${option.subtitle}. ${option.benefits.join(', ')}${option.recommended ? '. Recommended option' : ''}`;

          return (
          <m.button
            key={option.id}
            {...motion.stagger(index)}
            {...motion.noHover}
            onClick={() => handleTimingSelect(option.id)}
            aria-label={ariaLabel}
            className="timing-card w-full relative"
          >
            {option.recommended && (
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                <span className="bg-gradient-to-r from-orange-400 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {t('timing.recommended')}
                </span>
              </div>
            )}

            <div
              className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300"
              style={{
                borderColor: 'transparent',
                '--timing-color': theme.colors[option.colorType].base
              }}
            >
              <div
                className="h-2"
                style={{ background: theme.colors[option.colorType].gradient }}
              />
              <div className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: theme.colors[option.colorType].gradient }}
                  >
                    <SafeIcon icon={option.icon} className="w-6 h-6" style={{ color: theme.colors[option.colorType].contrastIcon }} aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{option.title}</h2>
                    <p className="text-gray-600">{option.subtitle}</p>
                  </div>
                </div>

                {/* Show benefits only on desktop */}
                <ul className="space-y-2 hidden md:block">
                  {option.benefits.map((benefit,idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: theme.colors.secondary.base }}
                      />
                      <span className="text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-end">
                    <span
                      className="font-medium"
                      style={{ color: theme.colors[option.colorType].base }}
                    >
                      {t('timing.select')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </m.button>
          );
        })}
      </div>
    </div>
  );
};

export default RentalTiming;